import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, FileSignature, Upload, Monitor, CheckSquare, BookOpen, FormInput, ClipboardCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import SignatureModal from "@/components/SignatureModal";

const ICONS = {
  E_SIGNATURE: FileSignature, DOCUMENT_UPLOAD: Upload, FORM_INPUT: FormInput,
  IT_PROVISION: Monitor, POLICY_ACK: CheckSquare, TRAINING: BookOpen,
};

export default function Onboarding() {
  const [list, setList] = useState([]);
  const [signTarget, setSignTarget] = useState(null); // { onboardingId, task, defaultName }
  const load = () => api.get("/onboarding/list").then(r => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const complete = async (onbId, taskId) => {
    try { await api.patch(`/onboarding/${onbId}/tasks/${taskId}`, { status: "COMPLETED" }); toast.success("Task completed"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="onboarding-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Onboarding</div>
          <h1 className="text-3xl font-bold font-display mt-1">Active Onboardings</h1>
          <p className="text-neutral-600 mt-1">{list.length} in progress. Tasks auto‑created when candidates are hired.</p>
        </div>
        <Link to="/app/onboarding/templates">
          <Button variant="outline" className="rounded-full" data-testid="onboarding-templates-link">
            <ClipboardCheck className="w-4 h-4 mr-1" /> Manage templates
          </Button>
        </Link>
      </div>
      {list.length === 0 && (
        <Card className="border-0 shadow-sm"><CardContent className="p-10 text-center text-neutral-500">
          No onboardings yet. Hire a candidate from Recruit to see one appear here.
        </CardContent></Card>
      )}
      <div className="space-y-4">
        {list.map(o => {
          const done = o.tasks.filter(t => t.status === "COMPLETED").length;
          const pct = Math.round(done / o.tasks.length * 100);
          const groups = {};
          o.tasks.forEach(t => { (groups[t.group] = groups[t.group] || []).push(t); });
          return (
            <Card key={o.onboarding_id} className="border-0 shadow-sm" data-testid={`onboarding-${o.onboarding_id}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">{o.name}</h3>
                    <div className="text-xs text-neutral-500">Started {new Date(o.created_at).toLocaleDateString()}</div>
                  </div>
                  <Badge className="bg-pink text-white border-0">{pct}% complete</Badge>
                </div>
                <Progress value={pct} className="mt-3" />
                <div className="mt-5 grid md:grid-cols-2 gap-4">
                  {Object.entries(groups).map(([g, ts]) => (
                    <div key={g}>
                      <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">{g}</div>
                      <div className="space-y-1.5">
                        {ts.map(t => {
                          const Icon = ICONS[t.type] || Circle;
                          const isDone = t.status === "COMPLETED";
                          const isSig = t.type === "E_SIGNATURE";
                          return (
                            <div key={t.task_id} className="flex items-center justify-between rounded-md p-2 hover:bg-neutral-50">
                              <div className="flex items-center gap-3 min-w-0">
                                {isDone ? <CheckCircle2 className="w-4 h-4 text-teal shrink-0" /> : <Icon className="w-4 h-4 text-neutral-400 shrink-0" />}
                                <div className={`text-sm ${isDone ? "line-through text-neutral-400" : ""}`}>
                                  {t.title}
                                  {isDone && t.signer_name && <span className="ml-2 text-xs text-neutral-500">· signed by {t.signer_name}</span>}
                                </div>
                              </div>
                              {!isDone && (
                                isSig
                                  ? <Button size="sm" className="btn-primary rounded-full text-xs" onClick={() => setSignTarget({ onboardingId: o.onboarding_id, task: t, defaultName: o.name })} data-testid={`sign-${t.task_id}`}>Sign now</Button>
                                  : <Button size="sm" variant="outline" className="text-xs" onClick={() => complete(o.onboarding_id, t.task_id)} data-testid={`task-complete-${t.task_id}`}>Mark done</Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <SignatureModal
        open={!!signTarget}
        onOpenChange={(v) => !v && setSignTarget(null)}
        onboardingId={signTarget?.onboardingId}
        task={signTarget?.task}
        defaultName={signTarget?.defaultName}
        onSigned={() => load()}
      />
    </div>
  );
}
